import React from 'react';
import {
  ToggleButtonGroup as MuiToggleButtonGroup,
  ToggleButtonGroupProps as MuiToggleButtonGroupProps,
} from '@mui/material';
import { PounceStyleProps, extractPounceProps, mapPouncePropsToSx } from '../../utils';

export interface ToggleButtonGroupProps<T = any>
  extends
    Omit<MuiToggleButtonGroupProps, 'value' | 'onChange' | keyof PounceStyleProps>,
    PounceStyleProps {
  /** The currently selected value */
  value: T;

  /** Callback fired when the selection changes */
  onChange: (event: React.MouseEvent<HTMLElement>, value: T) => void;

  /** The toggle buttons to render */
  children: React.ReactNode;

  /** Additional styles to apply (combined with pounce props) */
  sx?: MuiToggleButtonGroupProps['sx'];
}

/**
 * ToggleButtonGroup component built on top of MUI ToggleButtonGroup.
 *
 * A container for grouping related toggle buttons together. Manages the selection
 * state and propagates it to child ToggleButton components.
 *
 */
export const ToggleButtonGroup = React.forwardRef(function ToggleButtonGroup<T>(
  { children, value, onChange, sx, ...rest }: ToggleButtonGroupProps<T>,
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  const { pounceProps, otherProps } = extractPounceProps(rest);
  const pounceStyles = mapPouncePropsToSx(pounceProps);

  // Combine pounce-generated styles with user sx
  const combinedSx: MuiToggleButtonGroupProps['sx'] = {
    ...pounceStyles,
    ...(sx || {}),
  } as MuiToggleButtonGroupProps['sx'];

  // Handle change but prevent deselection (always require one selected)
  const handleChange = (event: React.MouseEvent<HTMLElement>, newValue: T) => {
    // Only call onChange if a value is selected (prevent deselection)
    if (newValue !== null) {
      onChange(event, newValue);
    }
  };

  return (
    <MuiToggleButtonGroup
      ref={ref}
      value={value}
      onChange={handleChange}
      exclusive
      sx={combinedSx}
      {...otherProps}
    >
      {children}
    </MuiToggleButtonGroup>
  );
}) as <T>(
  props: ToggleButtonGroupProps<T> & { ref?: React.ForwardedRef<HTMLDivElement> },
) => React.ReactElement;
